import { DownOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import styled from 'styled-components';

import KalibrrLogo from './KalibrrLogo';
import { Media } from './Media';

const FooterWrapper = styled.div`
  padding: 2rem;

  @media (min-width: 1024px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const FooterGroup = styled.div`
  display: flex;
  flex-direction: column-reverse;

  @media (min-width: 768px) {
    flex-direction: column;
  }
`;

const FooterSection = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
`;

const FooterLogo = styled.div`
  p {
    margin-top: 1rem;
  }

  @media (min-width: 768px) {
    margin: 4rem 0;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const FooterLinkSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ul {
    list-style-type: none;
    padding: 0;
  }
  ul.hidden {
    display: none;
  }

  .footer-link-item li {
    padding-top: 1rem;
  }
  .footer-link-item:last-of-type li {
    padding-bottom: 1rem;
  }

  a {
    color: var(--text-color);
  }
  a:hover {
    text-decoration: underline;
  }

  @media (min-width: 768px) {
    ul.hidden {
      display: block;
    }
  }

  @media (min-width: 1024px) {
    flex: 0.16;
  }
`;

const FooterLinkSectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 1rem;
  padding-bottom: 0.5rem;

  h6 {
    flex: 1;
    font-size: 1rem;
  }

  .collapse-indicator {
    font-size: 0.75rem;
    transition: transform 0.1s ease-in-out;
  }
  .rotate-180 {
    transform: rotate(180deg);
  }
`;

const FooterTerms = styled.div`
  font-size: 0.85rem;
  display: flex;
  justify-content: space-between;

  a:hover {
    text-decoration: underline;
  }

  @media (min-width: 768px) {
    width: 40%;
  }
`;

type FooterLink = {
  label: string;
  path: string;
};

type FooterLinkSection = {
  header: string;
  links: FooterLink[];
  isCollapsed: boolean;
};

const Footer: React.FC = () => {
  const footerLinks: { [key: string]: FooterLinkSection } = {
    candidates: {
      header: 'Candidates',
      links: [
        {
          label: 'Signup',
          path: 'https://www.kalibrr.com/candidate/signup?type=email',
        },
        {
          label: 'Testimonials',
          path: 'https://www.kalibrr.com/#testimonials',
        },
        { label: 'Job Board', path: 'https://www.kalibrr.com/job-board/1' },
      ],
      isCollapsed: true,
    },
    employer: {
      header: 'Employer',
      links: [
        {
          label: 'Start Hiring',
          path: 'https://www.kalibrr.com/employers/get-started',
        },
        { label: 'Kalibrr Sourcing', path: 'https://www.kalibrr.com/sourcing' },
        {
          label: 'Kalibrr Partnership',
          path: 'https://www.kalibrr.com/employers',
        },
        {
          label: 'Testimonials',
          path: 'https://www.kalibrr.com/employers/#testimonials',
        },
      ],
      isCollapsed: true,
    },
    about: {
      header: 'About',
      links: [
        { label: 'About', path: 'https://www.kalibrr.com/about' },
        { label: 'Careers', path: 'https://www.kalibrr.com/c/kalibrr-ph/jobs' },
        { label: 'Newsroom', path: 'https://www.kalibrr.com/about#newsroom' },
      ],
      isCollapsed: true,
    },
    support: {
      header: 'Support',
      links: [
        { label: 'Contact Us', path: 'https://www.kalibrr.com/contactus' },
        { label: 'Advice', path: 'https://www.kalibrr.com/advice' },
        { label: 'Blog', path: 'https://www.kalibrr.com/blog' },
      ],
      isCollapsed: true,
    },
  };

  const [sections, setSections] = useState(footerLinks);

  const handleSectionToggle = (key: string) => {
    const section = sections[key];
    section.isCollapsed = !section.isCollapsed;
    setSections({ ...sections });
  };

  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <FooterGroup>
        <FooterSection>
          <FooterLogo>
            <Media greaterThanOrEqual='md'>
              <KalibrrLogo variant='blue' />
            </Media>
            <Media lessThan='md'>
              <KalibrrLogo variant='gray' />
            </Media>
            <p>
              We transform the way candidates find jobs and companies hire
              talents.
            </p>
          </FooterLogo>
        </FooterSection>

        <FooterSection>
          <FooterLinks>
            {Object.keys(sections).map((key, index) => (
              <FooterLinkSection key={index}>
                <FooterLinkSectionHeader
                  onClick={() => handleSectionToggle(key)}
                >
                  <h6 className='text-primary'>{sections[key].header}</h6>
                  <Media lessThan='md'>
                    <DownOutlined
                      className={`collapse-indicator text-muted ${
                        sections[key].isCollapsed ? '' : 'rotate-180'
                      }`}
                    />
                  </Media>
                </FooterLinkSectionHeader>
                <ul className={sections[key].isCollapsed ? 'hidden' : ''}>
                  {sections[key].links.map((link, index) => (
                    <a
                      href={link.path}
                      key={index}
                      className='footer-link-item'
                    >
                      <li>{link.label}</li>
                    </a>
                  ))}
                </ul>
              </FooterLinkSection>
            ))}
          </FooterLinks>
        </FooterSection>
      </FooterGroup>

      <FooterSection>
        <FooterTerms className='text-muted'>
          <span>&copy; {currentYear} Kalibrr, Inc.</span>
          <span>
            <a href='https://www.kalibrr.com/privacy' className='text-muted'>
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href='https://www.kalibrr.com/terms' className='text-muted'>
              Terms of Use
            </a>
          </span>
        </FooterTerms>
      </FooterSection>
    </FooterWrapper>
  );
};

export default Footer;
